import { useQuery } from '@tanstack/react-query'
import { CheckCircle2, Circle, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { onboarding as api } from '@/api/endpoints'
import { useAuth } from '@/store/auth'

import { setOnboardingHidden, useOnboardingHidden } from './visibility'

/**
 * Kontrolný zoznam nastavenia školy (`apps.core.onboarding`) - nad
 * kalendárom, len pre správcu, novej aj prevzatej školy. Kroky sa počítajú z dát, takže hotový krok sa odškrtne
 * sám. Karta zmizne, keď je všetko hotové, alebo keď ju správca skryje
 * (pamätá si to prehliadač) - späť ju vráti ikonka v hlavičke
 * (`OnboardingButton`).
 */
export function OnboardingCard() {
  const { t } = useTranslation()
  const isAdmin = Boolean(useAuth((s) => s.user)?.can_administrate)
  const closed = useOnboardingHidden()
  const { data } = useQuery({
    queryKey: ['onboarding'],
    queryFn: api.get,
    enabled: isAdmin && !closed,
    staleTime: 60 * 1000,
  })

  if (!isAdmin || closed || !data || !data.show || data.done >= data.total) return null

  function close() {
    setOnboardingHidden(true)
  }

  return (
    <section className="card relative space-y-2 border-sky-200 bg-sky-50/50 p-4">
      <button
        type="button"
        onClick={close}
        className="absolute right-2 top-2 rounded p-1 text-slate-400 hover:bg-white hover:text-slate-700"
        title={t('onboarding.hide')}
        aria-label={t('onboarding.hide')}
      >
        <X size={16} />
      </button>
      <div>
        <h2 className="text-sm font-semibold">{t('onboarding.title')}</h2>
        <p className="text-xs text-slate-500">
          {t('onboarding.progress', { done: data.done, total: data.total })}
        </p>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-white">
        <div
          className="h-full rounded-full bg-emerald-500"
          style={{ width: `${(data.done / data.total) * 100}%` }}
        />
      </div>
      <ul className="grid gap-1 sm:grid-cols-2">
        {data.steps.map((step) => (
          <li key={step.key}>
            <Link
              to={step.link}
              className={
                step.done
                  ? 'flex items-center gap-2 rounded px-1 py-0.5 text-sm text-slate-400 line-through'
                  : 'flex items-center gap-2 rounded px-1 py-0.5 text-sm text-slate-700 hover:bg-white'
              }
            >
              {step.done ? (
                <CheckCircle2 size={15} className="shrink-0 text-emerald-600" />
              ) : (
                <Circle size={15} className="shrink-0 text-slate-400" />
              )}
              {t(`onboarding.steps.${step.key}`)}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}
